import { apiClient } from './client'
import type {
  ExecutiveReportData,
  ScoreWithTrend,
  FrameworkScoreSummary,
  OperationalMetrics,
  ScoreDataPoint,
} from '@/types/executiveReport'
import type { ApiResponse } from '@/types'

/**
 * Report generation/export surfaces: the legacy compliance report, baseline
 * report download, drift-audit export, and the Executive Report. Split out
 * of `lib/api.ts` (WO-10.4).
 */

// ── Executive Report normalization helpers ──
// The Container API may return PascalCase or camelCase keys depending on
// serializer settings; these helpers accept either form defensively.

function normalizeScoreWithTrend(s: Record<string, unknown> | undefined): ScoreWithTrend {
  if (!s) return { label: '', current: null, periodStart: null, change: null, direction: 'flat', weight: 0 }
  return {
    label: (s.Label as string) ?? (s.label as string) ?? '',
    current: (s.Current as number | null) ?? (s.current as number | null) ?? null,
    periodStart: (s.PeriodStart as number | null) ?? (s.periodStart as number | null) ?? null,
    change: (s.Change as number | null) ?? (s.change as number | null) ?? null,
    direction: (s.Direction as ScoreWithTrend['direction']) ?? (s.direction as ScoreWithTrend['direction']) ?? 'flat',
    weight: (s.Weight as number) ?? (s.weight as number) ?? 0,
  }
}

function normalizeFramework(f: Record<string, unknown>): FrameworkScoreSummary {
  return {
    code: (f.Code as string) ?? (f.code as string) ?? '',
    name: (f.Name as string) ?? (f.name as string) ?? '',
    score: (f.Score as number) ?? (f.score as number) ?? 0,
    passedControls: (f.PassedControls as number) ?? (f.passedControls as number) ?? 0,
    totalControls: (f.TotalControls as number) ?? (f.totalControls as number) ?? 0,
    periodStartScore: (f.PeriodStartScore as number | null) ?? (f.periodStartScore as number | null) ?? null,
    change: (f.Change as number | null) ?? (f.change as number | null) ?? null,
    direction: (f.Direction as FrameworkScoreSummary['direction']) ?? (f.direction as FrameworkScoreSummary['direction']) ?? 'flat',
    includeInGuardianScore: (f.IncludeInGuardianScore as boolean) ?? (f.includeInGuardianScore as boolean) ?? false,
  }
}

function normalizeMetrics(m: Record<string, unknown> | undefined): OperationalMetrics {
  if (!m) return { violationsResolved: 0, driftItemsRemediated: 0, waiversGranted: 0, openCriticalViolations: 0, openHighViolations: 0, totalDriftDetected: 0 }
  return {
    violationsResolved: (m.ViolationsResolved as number) ?? (m.violationsResolved as number) ?? 0,
    driftItemsRemediated: (m.DriftItemsRemediated as number) ?? (m.driftItemsRemediated as number) ?? 0,
    waiversGranted: (m.WaiversGranted as number) ?? (m.waiversGranted as number) ?? 0,
    openCriticalViolations: (m.OpenCriticalViolations as number) ?? (m.openCriticalViolations as number) ?? 0,
    openHighViolations: (m.OpenHighViolations as number) ?? (m.openHighViolations as number) ?? 0,
    totalDriftDetected: (m.TotalDriftDetected as number) ?? (m.totalDriftDetected as number) ?? 0,
  }
}

function normalizeDataPoint(p: Record<string, unknown>): ScoreDataPoint {
  return {
    date: (p.Date as string) ?? (p.date as string) ?? '',
    guardianScore: (p.GuardianScore as number | null) ?? (p.guardianScore as number | null) ?? null,
    secureScore: (p.SecureScore as number | null) ?? (p.secureScore as number | null) ?? null,
    bpaScore: (p.BpaScore as number | null) ?? (p.bpaScore as number | null) ?? null,
    mfaAdoption: (p.MfaAdoption as number | null) ?? (p.mfaAdoption as number | null) ?? null,
    baselineCompliance: (p.BaselineCompliance as number | null) ?? (p.baselineCompliance as number | null) ?? null,
  }
}

// Compliance APIs (legacy report generator)
//
// NOTE: no backend route matches `/compliance/{tenantId}/report` in the
// current Container (`StandardsComplianceController` mounts at
// `/api/compliance` but has no `report` action) — this is unreachable
// client code with zero callers in the frontend today, most likely a relic
// of the `ComplianceReports.tsx` dead page (WO-10 item 2). Left in place
// (out of scope for this split) but typed `unknown` since the payload shape
// is unverifiable against any real endpoint.
export async function getComplianceReport(tenantId: string, frameworkId?: string): Promise<unknown> {
  const params = frameworkId ? { frameworkId } : {}
  const response = await apiClient.get<ApiResponse<unknown>>(
    `/compliance/${tenantId}/report`,
    { params }
  )
  return response.data.data
}

export async function exportComplianceReport(tenantId: string, format: 'pdf' | 'excel' = 'pdf'): Promise<Blob> {
  const response = await apiClient.get(
    `/compliance/${tenantId}/export`,
    {
      params: { format },
      responseType: 'blob'
    }
  )
  return response.data
}

// Baseline Reports APIs
export async function downloadBaselineReport(tenantId: string, format: 'csv' | 'html' = 'csv'): Promise<Blob> {
  const response = await apiClient.get(
    `/reports/baseline/${tenantId}/download`,
    {
      params: { format },
      responseType: 'blob'
    }
  )
  return response.data
}

// Executive Report APIs
export async function getExecutiveReportData(tenantId: string, periodDays: number = 30): Promise<ExecutiveReportData> {
  const response = await apiClient.get(
    `/executive-report/${tenantId}/data`,
    { params: { period: periodDays } }
  )
  const d = response.data
  return {
    tenantId: d.TenantId ?? d.tenantId ?? '',
    tenantName: d.TenantName ?? d.tenantName ?? '',
    generatedAt: d.GeneratedAt ?? d.generatedAt ?? '',
    reportPeriodDays: d.ReportPeriodDays ?? d.reportPeriodDays ?? 30,
    currentGuardianScore: d.CurrentGuardianScore ?? d.currentGuardianScore ?? null,
    periodStartGuardianScore: d.PeriodStartGuardianScore ?? d.periodStartGuardianScore ?? null,
    letterGrade: d.LetterGrade ?? d.letterGrade ?? 'N/A',
    rating: d.Rating ?? d.rating ?? 'No Data',
    secureScore: normalizeScoreWithTrend(d.SecureScore ?? d.secureScore),
    bpaScore: normalizeScoreWithTrend(d.BpaScore ?? d.bpaScore),
    mfaAdoption: normalizeScoreWithTrend(d.MfaAdoption ?? d.mfaAdoption),
    baselineCompliance: normalizeScoreWithTrend(d.BaselineCompliance ?? d.baselineCompliance),
    frameworkScores: (d.FrameworkScores ?? d.frameworkScores ?? []).map(normalizeFramework),
    metrics: normalizeMetrics(d.Metrics ?? d.metrics),
    guardianScoreTrend: (d.GuardianScoreTrend ?? d.guardianScoreTrend ?? []).map(normalizeDataPoint),
    executiveNarrative: d.ExecutiveNarrative ?? d.executiveNarrative ?? null,
  }
}

export async function downloadExecutiveReport(
  tenantId: string, format: 'pdf' | 'html' = 'pdf', periodDays: number = 30
): Promise<Blob> {
  const response = await apiClient.get(
    `/executive-report/${tenantId}/download`,
    {
      params: { format, period: periodDays },
      responseType: 'blob'
    }
  )
  return response.data
}

export const reportsApi = {
  getComplianceReport,
  exportComplianceReport,
  downloadBaselineReport,
  getExecutiveReportData,
  downloadExecutiveReport,
}
